// A list of tasks (BL-004) and the form that adds one: on Inbox › Tasks, a customer's history and a
// job's sheet. Each task says what needs doing, when it's due, its type, who has it and what it's
// about; Done closes it (with a note), and it can be moved to in progress or waiting, changed, or a
// closed one reopened. The server checks every change against the version this list read.
// BL-189: a task is a RowList row: when it's due a word beside its name (bad once it's overdue), its
// state a SelectField under it, Done… and Change (or Reopen) its actions, and the forms that close or
// change it a part under it, from TextField, SelectField, Button and Link. Tasks' ListPage says the
// list's loading, empty and failed itself (useTaskList, TaskAdd, TaskRows); a customer's record and a
// job's sheet draw TaskList, which reads its own.

import { useState } from 'react';
import {
  Button,
  Link,
  RowList,
  SelectField,
  TextField,
  type Row,
  type RowAction,
  type RowLine,
} from '@flowology/ui-web/fl';
import { Section } from '@flowology/ui-web/patterns';
import { today as localToday } from '../book-model.ts';
import { customerHref } from '../history/customer-link.ts';
import { useAct, useQuery } from '../books/ui.tsx';
import { usePaged } from '../blueprint/paged.ts';
import { shownText } from '../shared/pages.ts';
import { Loading } from '../shared/Loading.tsx';
import { useSaveKey } from '../shared/save-key.ts';
import {
  closedText,
  createInput,
  draftProblem,
  dueWords,
  grouped,
  isOpen,
  STATUS_LABEL,
  taskMeta,
  tasksInput,
  type Task,
  type TaskPage,
  type TaskDraft,
  type TaskType,
} from './tasks-model.ts';
import './task-list.css';

export interface TaskQuery {
  assignee?: 'me' | 'unassigned' | string;
  state?: 'open' | 'closed' | 'all';
  customerId?: string;
  jobId?: string;
}

/** A page of tasks at a time, "Show more" reading on (list caps, BL-110): an office's open tasks pass a page. */
export function useTaskList(query: TaskQuery) {
  return usePaged<Task, TaskPage>('tasks.list', tasksInput({ ...query }), (p) => p.tasks);
}
export type TaskListRead = ReturnType<typeof useTaskList>;

export interface TaskListProps {
  /** The list's name, as a screen reader hears it ("Your tasks", "Tasks for this job"). */
  title: string;
  query: TaskQuery;
  /** What a task added here is about. */
  about?: { customerId?: string; jobId?: string };
  timeZone: string;
  canAdd: boolean;
  /** Inbox: in groups by when they're due. */
  groupByDue?: boolean;
  /** What each task says it's about: all of it, or not what the place already is (a customer's record, a job's sheet). */
  showAbout?: 'all' | 'job' | 'none';
  empty: string;
  /** A task added here goes to this person unless the form says otherwise (Yours: the person's own). */
  defaultAssignee?: string;
  /** Opens a task's job (the job sheet); without it the job is named, not a link. */
  onOpenJob?: (jobId: string) => void;
  onChanged?: () => void;
}

/** A customer's record and a job's sheet: the list reads itself, Add a task over it, what was done under it. */
export function TaskList(props: TaskListProps) {
  const list = useTaskList(props.query);
  const [said, setSaid] = useState('');
  const changed = () => {
    list.reload();
    props.onChanged?.();
  };
  return (
    <div className="tl">
      {props.canAdd && (
        <TaskAdd
          about={props.about ?? {}}
          assignee={props.defaultAssignee ?? ''}
          onAdded={(s) => {
            setSaid(s);
            changed();
          }}
        />
      )}
      {!list.rows ? (
        <Loading error={list.error} what="the tasks" retry={list.reload} />
      ) : !list.rows.length ? (
        <TaskEmpty text={props.empty} />
      ) : (
        <TaskRows {...props} list={list} onSaid={setSaid} />
      )}
      <TaskSaid said={said} />
    </div>
  );
}

/** No tasks here: one muted sentence, in the list's place. */
export function TaskEmpty({ text }: { text: string }) {
  return <p className="tl__empty">{text}</p>;
}

/** What the last change did ("Task done."), for everyone and for screen readers. */
export function TaskSaid({ said }: { said: string }) {
  return (
    <p className="tl__said" role="status">
      {said}
    </p>
  );
}

/** The tasks read so far, one a row, in groups by when they're due on Inbox; the next page under them. */
export function TaskRows({
  list,
  title,
  query,
  timeZone,
  groupByDue = false,
  showAbout = 'all',
  onOpenJob,
  onChanged,
  onSaid,
}: TaskListProps & { list: TaskListRead; onSaid: (said: string) => void }) {
  const act = useAct(onSaid, () => {
    list.reload();
    onChanged?.();
  });
  // the one task whose form is open under it: closing it, or changing it
  const [open, setOpen] = useState<{ id: string; form: 'close' | 'edit' } | null>(null);
  const today = localToday(timeZone);
  const tasks = list.rows ?? [];
  const total = list.first?.total ?? tasks.length;
  const rows = (ts: readonly Task[]) =>
    ts.map((t) =>
      taskRow(t, {
        today,
        timeZone,
        showAbout,
        busy: act.busy,
        form: open?.id === t.id ? open.form : null,
        setForm: (form) => setOpen(form ? { id: t.id, form } : null),
        act,
        ...(onOpenJob ? { onOpenJob } : {}),
      }),
    );
  // the next page, under the last of them, with how many of how many are shown
  const more = {
    ...(list.more ? { onMore: () => list.more?.() } : {}),
    ...(list.more || list.busy ? { note: shownText(tasks.length, total) } : {}),
  };
  const groups = groupByDue && query.state !== 'closed' ? grouped(tasks, today) : null;
  return (
    <>
      {groups ? (
        groups.map((g, i) => (
          // a group inside the list is a Section: a hairline, its name and count, never a box of its own
          <Section key={g.group} title={g.group} count={g.tasks.length}>
            <RowList label={g.group} rows={rows(g.tasks)} {...(i === groups.length - 1 ? more : {})} />
          </Section>
        ))
      ) : (
        <RowList label={title} rows={rows(tasks)} {...more} />
      )}
      {list.error && tasks.length > 0 && (
        <p className="tl__bad" role="alert">
          {list.error}
        </p>
      )}
    </>
  );
}

const STATES = (['open', 'in_progress', 'waiting'] as const).map((s) => ({
  value: s,
  label: STATUS_LABEL[s],
}));

/** One task as a RowList row: its due word beside it, its lines, its state, its form when one is open. */
function taskRow(
  t: Task,
  o: {
    today: string;
    timeZone: string;
    showAbout: 'all' | 'job' | 'none';
    busy: boolean;
    form: 'close' | 'edit' | null;
    setForm: (form: 'close' | 'edit' | null) => void;
    act: ReturnType<typeof useAct>;
    onOpenJob?: (jobId: string) => void;
  },
): Row {
  const due = dueWords(t, o.today, o.timeZone);
  const customer = o.showAbout === 'all' ? t.customer : null;
  const job = o.showAbout === 'none' ? null : t.job;
  const open = isOpen(t);
  const { act, onOpenJob } = o;
  const lines: RowLine[] = [{ text: taskMeta(t) }];
  if (customer || job)
    lines.push({
      part: (
        <span className="tl__about">
          {customer && <Link href={customerHref(customer.id)}>{customer.name}</Link>}
          {customer && job && ' · '}
          {job &&
            (onOpenJob ? (
              <Link onPress={() => onOpenJob(job.id)}>{`Job #${job.number}`}</Link>
            ) : (
              <span>Job #{job.number}</span>
            ))}
        </span>
      ),
    });
  if (t.details) lines.push({ part: <span className="tl__details">{t.details}</span> });
  if (!open) lines.push({ text: closedText(t) });
  if (o.form === 'edit')
    lines.push({
      part: (
        <TaskEdit
          task={t}
          onDone={async (input) => {
            o.setForm(null);
            if (input) await act.run('tasks.update', input, 'Task changed.');
          }}
        />
      ),
    });
  else if (o.form === 'close')
    lines.push({
      part: (
        <CloseForm
          task={t}
          busy={o.busy}
          onClose={(outcome, note) =>
            void act
              .run(
                'tasks.close',
                { id: t.id, rowVersion: t.rowVersion, outcome, ...(note ? { note } : {}) },
                outcome === 'done' ? 'Task done.' : 'Task cancelled.',
              )
              .then(() => o.setForm(null))
          }
          onBack={() => o.setForm(null)}
        />
      ),
    });
  else if (open)
    lines.push({
      part: (
        <span className="tl__state">
          <SelectField
            label={`State of “${t.title}”`}
            hideLabel
            value={t.status}
            options={STATES}
            isDisabled={o.busy}
            onChange={(v) =>
              void act.run(
                'tasks.update',
                { id: t.id, rowVersion: t.rowVersion, status: v },
                `Task set to ${STATUS_LABEL[v as Task['status']].toLowerCase()}.`,
              )
            }
          />
        </span>
      ),
    });
  const actions: RowAction[] = o.form
    ? []
    : open
      ? [
          {
            id: 'done',
            text: 'Done…',
            label: `Done: ${t.title}`,
            isDisabled: o.busy,
            onPress: () => o.setForm('close'),
          },
          { id: 'change', text: 'Change', label: `Change “${t.title}”`, onPress: () => o.setForm('edit') },
        ]
      : [
          {
            id: 'reopen',
            text: 'Reopen',
            label: `Reopen “${t.title}”`,
            isDisabled: o.busy,
            onPress: () =>
              void act.run(
                'tasks.update',
                { id: t.id, rowVersion: t.rowVersion, status: 'open' },
                'Task reopened.',
              ),
          },
        ];
  return {
    id: t.id,
    name: t.title,
    label: t.title,
    ...(due ? { word: { text: due, tone: t.overdue ? ('bad' as const) : ('ink' as const) } } : {}),
    lines,
    ...(actions.length ? { actions, atRest: 2 as const } : {}),
  };
}

/** Done…: how it ended, then Done, Not needed (cancelled), or Back. */
function CloseForm({
  task: t,
  busy,
  onClose,
  onBack,
}: {
  task: Task;
  busy: boolean;
  onClose: (outcome: 'done' | 'cancelled', note: string) => void;
  onBack: () => void;
}) {
  const [note, setNote] = useState('');
  return (
    <form
      className="tl-form"
      aria-label={`Close “${t.title}”`}
      onSubmit={(e) => {
        e.preventDefault();
        onClose('done', note.trim());
      }}
    >
      <span className="tl-form__full">
        <TextField label="How it ended (optional)" value={note} maxLength={1000} onChange={setNote} />
      </span>
      <span className="tl-form__acts">
        <Button variant="primary" type="submit" isDisabled={busy}>
          Done
        </Button>
        <Link isDisabled={busy} onPress={() => onClose('cancelled', note.trim())}>
          Not needed
        </Link>
        <Link onPress={onBack}>Back</Link>
      </span>
    </form>
  );
}

function useTypesAndStaff() {
  const types = useQuery<{ types: TaskType[] }>('taskTypes.list', {});
  const staff = useQuery<{ staff: { id: string; displayName: string; isTechnician: boolean }[] }>(
    'staff.list',
    {},
  );
  return { types: types.data?.types ?? null, staff: staff.data?.staff ?? [], error: types.error };
}

function DraftFields({
  draft,
  set,
  types,
  staff,
}: {
  draft: TaskDraft;
  set: (d: Partial<TaskDraft>) => void;
  types: TaskType[];
  staff: { id: string; displayName: string }[];
}) {
  return (
    <>
      <SelectField
        label="Kind"
        value={draft.typeCode}
        onChange={(v) => set({ typeCode: v })}
        options={[
          ...(draft.typeCode ? [] : [{ value: '', label: 'Pick one' }]),
          ...types.map((t) => ({ value: t.code, label: t.name })),
        ]}
      />
      <span className="tl-form__full">
        <TextField
          label="What needs doing"
          value={draft.title}
          maxLength={200}
          onChange={(v) => set({ title: v })}
        />
      </span>
      <TextField label="Due" type="date" value={draft.dueOn} onChange={(v) => set({ dueOn: v })} />
      <SelectField
        label="Who"
        value={draft.assigneeStaffId}
        onChange={(v) => set({ assigneeStaffId: v })}
        options={[
          { value: '', label: 'Nobody yet' },
          ...staff.map((s) => ({ value: s.id, label: s.displayName })),
        ]}
      />
      <SelectField
        label="Priority"
        value={draft.priority}
        onChange={(v) => set({ priority: v as TaskDraft['priority'] })}
        options={[
          { value: 'high', label: 'High' },
          { value: 'normal', label: 'Normal' },
          { value: 'low', label: 'Low' },
        ]}
      />
      <span className="tl-form__full">
        <TextField
          label="Details (optional)"
          multiline
          value={draft.details}
          maxLength={2000}
          onChange={(v) => set({ details: v })}
        />
      </span>
    </>
  );
}

const BLANK: TaskDraft = {
  typeCode: '',
  title: '',
  details: '',
  dueOn: '',
  priority: 'normal',
  assigneeStaffId: '',
};

/** Add a task: the link, and the form in its place while it's open. */
export function TaskAdd({
  about,
  assignee,
  onAdded,
}: {
  about: { customerId?: string; jobId?: string };
  assignee: string;
  onAdded: (said: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  return adding ? (
    <TaskForm
      about={about}
      assignee={assignee}
      onDone={async (said) => {
        setAdding(false);
        if (said) onAdded(said);
      }}
    />
  ) : (
    <p className="tl__add">
      <Link onPress={() => setAdding(true)}>Add a task</Link>
    </p>
  );
}

function TaskForm({
  about,
  assignee,
  onDone,
}: {
  about: { customerId?: string; jobId?: string };
  assignee: string;
  onDone: (said: string | null) => Promise<void>;
}) {
  const { types, staff, error } = useTypesAndStaff();
  const [draft, setDraft] = useState<TaskDraft>({ ...BLANK, assigneeStaffId: assignee });
  const [why, setWhy] = useState<string | null>(null);
  const act = useAct(setWhy, () => undefined);
  const key = useSaveKey(JSON.stringify(draft));
  if (!types) return <Loading error={error} what="the task types" />;
  const save = async () => {
    const problem = draftProblem(draft);
    if (problem) return setWhy(problem);
    setWhy(null);
    const r = await act.run('tasks.create', createInput(key.key, draft, about), undefined, {
      idempotencyKey: key.key,
    });
    if (r) {
      key.renew();
      await onDone('Task added.');
    }
  };
  return (
    <form
      className="tl-form"
      aria-label="Add a task"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <DraftFields draft={draft} set={(d) => setDraft({ ...draft, ...d })} types={types} staff={staff} />
      <span className="tl-form__acts">
        <Button variant="primary" type="submit" isBusy={act.busy}>
          {act.busy ? 'Adding…' : 'Add the task'}
        </Button>
        <Link onPress={() => void onDone(null)}>Cancel</Link>
      </span>
      {why && (
        <p className="tl__bad tl-form__full" role="alert">
          {why}
        </p>
      )}
    </form>
  );
}

function TaskEdit({
  task: t,
  onDone,
}: {
  task: Task;
  onDone: (input: Record<string, unknown> | null) => Promise<void>;
}) {
  const { types, staff, error } = useTypesAndStaff();
  const [draft, setDraft] = useState<TaskDraft>({
    typeCode: t.type.code,
    title: t.title,
    details: t.details ?? '',
    dueOn: t.dueOn ?? '',
    priority: t.priority,
    assigneeStaffId: t.assignee?.id ?? '',
  });
  const [why, setWhy] = useState<string | null>(null);
  if (!types) return <Loading error={error} what="the task types" />;
  // a type switched off since stays pickable for the task that has it
  const shown = types.some((y) => y.code === t.type.code)
    ? types
    : [...types, { code: t.type.code, name: t.type.name, active: false }];
  return (
    <form
      className="tl-form"
      aria-label={`Change “${t.title}”`}
      onSubmit={(e) => {
        e.preventDefault();
        const problem = draftProblem(draft);
        if (problem) return setWhy(problem);
        void onDone({
          id: t.id,
          rowVersion: t.rowVersion,
          typeCode: draft.typeCode,
          title: draft.title.trim(),
          details: draft.details.trim() || null,
          dueOn: draft.dueOn || null,
          priority: draft.priority,
          assigneeStaffId: draft.assigneeStaffId || null,
        });
      }}
    >
      <DraftFields draft={draft} set={(d) => setDraft({ ...draft, ...d })} types={shown} staff={staff} />
      <span className="tl-form__acts">
        <Button variant="primary" type="submit">
          Save
        </Button>
        <Link onPress={() => void onDone(null)}>Cancel</Link>
      </span>
      {why && (
        <p className="tl__bad tl-form__full" role="alert">
          {why}
        </p>
      )}
    </form>
  );
}
